import {
  AUTO_SIZE_BINDING_MIN_TEXT_PADDING,
  inferAutoSizeBindingModeForShape,
  sanitizeAutoSizeBinding,
} from '../presets/primitiveAutoSizeBindingUtils';

const BOX_TYPES = new Set(['rect', 'roundedRect', 'parallelogram']);

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const roundTo = (value, digits = 2) => {
  const factor = 10 ** digits;
  return Math.round(value * factor) / factor;
};

const toNumber = (value, fallback = 0) => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : fallback;
};

const normalizeBounds = (rawBounds) => {
  if (!rawBounds || typeof rawBounds !== 'object') return null;
  if (
    Number.isFinite(rawBounds.minX)
    && Number.isFinite(rawBounds.minY)
    && Number.isFinite(rawBounds.maxX)
    && Number.isFinite(rawBounds.maxY)
  ) {
    const minX = Number(rawBounds.minX);
    const minY = Number(rawBounds.minY);
    const maxX = Number(rawBounds.maxX);
    const maxY = Number(rawBounds.maxY);
    return {
      minX,
      minY,
      maxX,
      maxY,
      width: Math.max(0, Number.isFinite(rawBounds.width) ? Number(rawBounds.width) : maxX - minX),
      height: Math.max(0, Number.isFinite(rawBounds.height) ? Number(rawBounds.height) : maxY - minY),
    };
  }
  if (
    Number.isFinite(rawBounds.x)
    && Number.isFinite(rawBounds.y)
    && Number.isFinite(rawBounds.width)
    && Number.isFinite(rawBounds.height)
  ) {
    const x = Number(rawBounds.x);
    const y = Number(rawBounds.y);
    const width = Math.max(0, Number(rawBounds.width));
    const height = Math.max(0, Number(rawBounds.height));
    return {
      minX: x,
      minY: y,
      maxX: x + width,
      maxY: y + height,
      width,
      height,
    };
  }
  return null;
};

const chooseDominantSignedDelta = (values) => {
  const candidates = (Array.isArray(values) ? values : []).filter((value) => Number.isFinite(value));
  if (!candidates.length) return 0;
  let winner = candidates[0];
  for (let index = 1; index < candidates.length; index += 1) {
    const value = candidates[index];
    if (Math.abs(value) > Math.abs(winner)) winner = value;
  }
  return winner;
};

const getPrimitiveMinWidth = (shape) => {
  if (shape?.type === 'line') return 1;
  if (shape?.type === 'circle') return 8;
  return 8;
};

const getPrimitiveMinHeight = (shape) => {
  if (shape?.type === 'line') return Math.max(1, toNumber(shape?.strokeWidth, 1));
  if (shape?.type === 'circle') return 8;
  return 8;
};

const getBoxEdges = (shape) => {
  const x = toNumber(shape?.x, 0);
  const y = toNumber(shape?.y, 0);
  const width = Math.max(getPrimitiveMinWidth(shape), toNumber(shape?.width, 220));
  const height = Math.max(getPrimitiveMinHeight(shape), toNumber(shape?.height, 80));
  return {
    left: x,
    top: y,
    right: x + width,
    bottom: y + height,
  };
};

const applyBoxEdges = (shape, edges, limits) => {
  const maxWidth = Math.max(getPrimitiveMinWidth(shape), toNumber(limits?.maxWidth, Infinity));
  const maxHeight = Math.max(getPrimitiveMinHeight(shape), toNumber(limits?.maxHeight, Infinity));
  const width = clamp(edges.right - edges.left, getPrimitiveMinWidth(shape), maxWidth);
  const height = clamp(edges.bottom - edges.top, getPrimitiveMinHeight(shape), maxHeight);
  return {
    ...shape,
    x: roundTo(edges.left, 2),
    y: roundTo(edges.top, 2),
    width: roundTo(width, 2),
    height: roundTo(height, 2),
  };
};

const resizeBoundBoxShapeFromHandle = ({
  shape,
  sourceTextBounds,
  handleId,
  deltaAxes,
  limits,
}) => {
  if (!BOX_TYPES.has(shape?.type)) return null;
  const textBounds = normalizeBounds(sourceTextBounds);
  if (!textBounds || !handleId) return null;
  const minPad = AUTO_SIZE_BINDING_MIN_TEXT_PADDING;

  const minWidth = getPrimitiveMinWidth(shape);
  const minHeight = getPrimitiveMinHeight(shape);
  const start = getBoxEdges(shape);
  let { left, right, top, bottom } = start;

  if (handleId.includes('e')) {
    right = start.right + toNumber(deltaAxes?.x, 0);
    right = Math.max(right, textBounds.maxX + minPad);
    right = Math.max(right, left + minWidth);
  }
  if (handleId.includes('w')) {
    left = start.left + toNumber(deltaAxes?.x, 0);
    left = Math.min(left, textBounds.minX - minPad);
    left = Math.min(left, right - minWidth);
  }
  if (handleId.includes('s')) {
    bottom = start.bottom + toNumber(deltaAxes?.y, 0);
    bottom = Math.max(bottom, textBounds.maxY + minPad);
    bottom = Math.max(bottom, top + minHeight);
  }
  if (handleId.includes('n')) {
    top = start.top + toNumber(deltaAxes?.y, 0);
    top = Math.min(top, textBounds.minY - minPad);
    top = Math.min(top, bottom - minHeight);
  }

  return applyBoxEdges(shape, { left, right, top, bottom }, limits);
};

const getLineEdges = (shape) => {
  const width = Math.max(1, toNumber(shape?.width, 220));
  const strokeWidth = Math.max(1, toNumber(shape?.strokeWidth, 1));
  const height = Math.max(strokeWidth, toNumber(shape?.height, 4));
  const x = toNumber(shape?.x, 0);
  const y = toNumber(shape?.y, 0);
  return {
    left: x,
    right: x + width,
    top: y - height / 2,
    bottom: y + height / 2,
    strokeWidth,
  };
};

const applyLineEdges = (shape, edges, limits) => {
  const maxWidth = Math.max(1, toNumber(limits?.maxWidth, Infinity));
  const maxHeight = Math.max(1, toNumber(limits?.maxHeight, Infinity));
  const strokeWidth = Math.max(1, toNumber(shape?.strokeWidth, 1));
  const width = clamp(edges.right - edges.left, 1, maxWidth);
  const height = clamp(edges.bottom - edges.top, strokeWidth, maxHeight);
  return {
    ...shape,
    x: roundTo(edges.left, 2),
    y: roundTo(edges.top + height / 2, 2),
    width: roundTo(width, 2),
    height: roundTo(height, 2),
  };
};

const resizeBoundLineFromHandle = ({
  shape,
  sourceTextBounds,
  handleId,
  deltaAxes,
  limits,
}) => {
  const textBounds = normalizeBounds(sourceTextBounds);
  if (!textBounds || shape?.type !== 'line' || !handleId) return null;

  const start = getLineEdges(shape);
  let { left, right, top, bottom } = start;
  const minWidth = 1;
  const minHeight = Math.max(1, start.strokeWidth);

  if (handleId.includes('e')) {
    right = start.right + toNumber(deltaAxes?.x, 0);
    right = Math.max(right, textBounds.maxX);
    right = Math.max(right, left + minWidth);
  }
  if (handleId.includes('w')) {
    left = start.left + toNumber(deltaAxes?.x, 0);
    left = Math.min(left, textBounds.minX);
    left = Math.min(left, right - minWidth);
  }
  if (handleId.includes('s')) {
    bottom = start.bottom + toNumber(deltaAxes?.y, 0);
    bottom = Math.max(bottom, top + minHeight);
  }
  if (handleId.includes('n')) {
    top = start.top + toNumber(deltaAxes?.y, 0);
    top = Math.min(top, bottom - minHeight);
  }

  return applyLineEdges(shape, { left, right, top, bottom }, limits);
};

const getCircleState = (shape) => {
  const size = Math.max(8, toNumber(shape?.width ?? shape?.height, 90));
  const x = toNumber(shape?.x, 0);
  const y = toNumber(shape?.y, 0);
  return {
    centerX: x + size / 2,
    centerY: y + size / 2,
    diameter: size,
    radius: size / 2,
  };
};

const getRequiredCircleDiameterForText = (circleState, sourceTextBounds) => {
  const textBounds = normalizeBounds(sourceTextBounds);
  if (!textBounds) return 8;
  const minPad = AUTO_SIZE_BINDING_MIN_TEXT_PADDING;
  const halfX = Math.max(
    circleState.centerX - textBounds.minX,
    textBounds.maxX - circleState.centerX,
    0,
  );
  const halfY = Math.max(
    circleState.centerY - textBounds.minY,
    textBounds.maxY - circleState.centerY,
    0,
  );
  return Math.max(8, (halfX + minPad) * 2, (halfY + minPad) * 2);
};

const getCircleEdgeDelta = (handleId, deltaAxes) => {
  if (!handleId) return 0;
  const dx = toNumber(deltaAxes?.x, 0);
  const dy = toNumber(deltaAxes?.y, 0);
  if (handleId.length === 1) {
    if (handleId === 'e') return dx;
    if (handleId === 'w') return -dx;
    if (handleId === 's') return dy;
    if (handleId === 'n') return -dy;
    return 0;
  }
  const candidates = [];
  if (handleId.includes('e')) candidates.push(dx);
  if (handleId.includes('w')) candidates.push(-dx);
  if (handleId.includes('s')) candidates.push(dy);
  if (handleId.includes('n')) candidates.push(-dy);
  return chooseDominantSignedDelta(candidates);
};

const resizeBoundCircleFromHandle = ({
  shape,
  sourceTextBounds,
  handleId,
  deltaAxes,
  limits,
}) => {
  if (shape?.type !== 'circle' || !handleId) return null;
  const circle = getCircleState(shape);
  const minDiameter = getRequiredCircleDiameterForText(circle, sourceTextBounds);
  const maxDiameter = Math.max(8, toNumber(limits?.maxDiameter, Infinity));
  const edgeDelta = getCircleEdgeDelta(handleId, deltaAxes);
  const nextRadius = clamp(circle.radius + edgeDelta, minDiameter / 2, maxDiameter / 2);
  const nextDiameter = nextRadius * 2;
  return {
    ...shape,
    x: roundTo(circle.centerX - nextRadius, 2),
    y: roundTo(circle.centerY - nextRadius, 2),
    width: roundTo(nextDiameter, 2),
    height: roundTo(nextDiameter, 2),
  };
};

export const resolveBoundResizeGesture = ({
  selectedPrimitiveId,
  handleId,
  primitive,
  sourceTextGeometry,
}) => {
  if (!selectedPrimitiveId || !handleId || !primitive) return null;
  const binding = sanitizeAutoSizeBinding(primitive.autoSizeBinding, primitive);
  if (!binding || binding.enabled === false) return null;
  const mode = inferAutoSizeBindingModeForShape(primitive);
  if (!mode || mode !== binding.mode) return null;
  if (sourceTextGeometry && !normalizeBounds(sourceTextGeometry?.contentLocalBounds || null)) return null;
  return {
    targetPrimitiveId: selectedPrimitiveId,
    sourceTextId: binding.sourceTextId,
    sourceTextIds: Array.isArray(binding.sourceTextIds) ? [...binding.sourceTextIds] : [binding.sourceTextId],
    handleId,
    shapeType: primitive.type,
    bindingMode: binding.mode,
  };
};

export const applyBoundShapeHandleResize = ({
  shape,
  binding,
  sourceTextBounds,
  handleId,
  deltaAxes,
  canvasWidth = Infinity,
  canvasHeight = Infinity,
}) => {
  const sanitized = sanitizeAutoSizeBinding(binding || shape?.autoSizeBinding, shape);
  const textBounds = normalizeBounds(sourceTextBounds);
  if (!sanitized || !textBounds || !handleId) return null;

  const limits = {
    maxWidth: Math.max(1, toNumber(canvasWidth, Infinity)),
    maxHeight: Math.max(1, toNumber(canvasHeight, Infinity)),
    maxDiameter: Math.max(1, Math.max(toNumber(canvasWidth, Infinity), toNumber(canvasHeight, Infinity))),
  };

  if (sanitized.mode === 'circle') {
    return resizeBoundCircleFromHandle({
      shape: { ...shape, autoSizeBinding: sanitized },
      sourceTextBounds: textBounds,
      handleId,
      deltaAxes,
      limits,
    });
  }
  if (sanitized.mode === 'line') {
    return resizeBoundLineFromHandle({
      shape: { ...shape, autoSizeBinding: sanitized },
      sourceTextBounds: textBounds,
      handleId,
      deltaAxes,
      limits,
    });
  }
  return resizeBoundBoxShapeFromHandle({
    shape: { ...shape, autoSizeBinding: sanitized },
    sourceTextBounds: textBounds,
    handleId,
    deltaAxes,
    limits,
  });
};
